import { useState, useEffect } from 'react';
import { Gift, Users, Copy, Share, Trophy, Loader2, Mail, DollarSign } from 'lucide-react';
import { Button } from '../ui/button.js';
import { Input } from '../ui/input.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { settingsService } from '../../services/settingsService.js';
import type { ReferralData, ReferralInvite } from '../../services/settingsService.js';
import { toast } from 'react-hot-toast';

const ReferralsTab = () => {
  const [referralData, setReferralData] = useState<ReferralData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);

  useEffect(() => {
    loadReferralData();
  }, []);

  const loadReferralData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await settingsService.loadReferralData();
      setReferralData(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load referral data';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = async () => {
    if (!referralData?.referralCode) return;
    
    try {
      await navigator.clipboard.writeText(referralData.referralCode);
      toast.success('Referral code copied to clipboard');
    } catch {
      toast.error('Failed to copy referral code');
    }
  };

  const shareReferral = async () => {
    if (!referralData?.referralCode) return;
    
    const shareText = `Join Beehive with my referral code: ${referralData.referralCode}`;
    const shareUrl = `https://beehive.ai/signup?ref=${referralData.referralCode}`;
    
    try {
      if (navigator.share) {
        await navigator.share({ 
          text: shareText,
          url: shareUrl
        });
      } else {
        await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
        toast.success('Referral link copied to clipboard');
      }
    } catch {
      toast.error('Failed to share referral');
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail || !referralData?.referralCode) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSendingInvite(true);
    try {
      const invite: ReferralInvite = {
        email: inviteEmail,
        message: inviteMessage || `Join Beehive with my referral code: ${referralData.referralCode}`
      };
      
      await settingsService.sendReferralInvite(invite);
      toast.success(`Referral invite sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteMessage('');
      setShowInviteForm(false);
      await loadReferralData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send referral invite';
      toast.error(errorMessage);
    } finally {
      setSendingInvite(false);
    }
  };

  const activateReferral = async (referralId: string) => {
    try {
      const result = await settingsService.activateReferral(referralId);
      toast.success(`Referral activated! You earned $${result.earnings}`);
      await loadReferralData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to activate referral';
      toast.error(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">Referrals</h2>
          <p className="text-slate-400 mt-1">Earn rewards by inviting friends to Beehive</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 size={32} className="animate-spin text-brand-accent" />
          <span className="ml-3 text-slate-400">Loading referral data...</span>
        </div>
      </div>
    );
  }

  if (error || !referralData) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">Referrals</h2>
          <p className="text-slate-400 mt-1">Earn rewards by inviting friends to Beehive</p>
        </div>
        <div className="p-6 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400">{error || 'Failed to load referral data'}</p>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={loadReferralData}
            className="mt-3"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">Referrals</h2>
        <p className="text-slate-400 mt-1">Earn rewards by inviting friends to Beehive</p>
      </div>

      <Card className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Gift size={20} className="text-brand-accent" />
            <span>Referral Program</span>
          </CardTitle>
          <CardDescription>Share Beehive and earn $30 for each successful referral</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center p-6 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-xl">
            <div className="text-3xl font-bold text-brand-accent mb-2">${referralData.stats.totalEarnings}</div>
            <div className="text-sm text-slate-400 mb-4">Total Earnings</div>
            <div className="text-lg font-semibold text-brand-text">${referralData.stats.thisMonthEarnings} this month</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
              <div className="text-2xl font-bold text-blue-400">{referralData.stats.totalReferrals}</div>
              <div className="text-sm text-slate-400">Total Referrals</div>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
              <div className="text-2xl font-bold text-green-400">{referralData.stats.successfulReferrals}</div>
              <div className="text-sm text-slate-400">Successful</div>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
              <div className="text-2xl font-bold text-yellow-400">{referralData.stats.pendingReferrals}</div>
              <div className="text-sm text-slate-400">Pending</div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Your Referral Code</label>
            <div className="flex space-x-2">
              <Input
                value={referralData.referralCode}
                readOnly
                className="violett-input flex-1"
              />
              <Button onClick={copyReferralCode} variant="ghost">
                <Copy size={16} />
              </Button>
              <Button onClick={shareReferral} variant="ghost">
                <Share size={16} />
              </Button>
              <Button 
                onClick={() => setShowInviteForm(!showInviteForm)} 
                variant="ghost"
              >
                <Mail size={16} />
              </Button>
            </div>

            {showInviteForm && (
              <div className="mt-4 p-4 glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Email Address</label>
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="friend@example.com"
                    className="violett-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Custom Message (Optional)</label>
                  <Input
                    value={inviteMessage}
                    onChange={(e) => setInviteMessage(e.target.value)}
                    placeholder="Join me on Beehive!"
                    className="violett-input"
                  />
                </div>
                <div className="flex space-x-2">
                  <Button 
                    onClick={sendInvite}
                    disabled={sendingInvite || !inviteEmail}
                    size="sm"
                  >
                    {sendingInvite ? (
                      <>
                        <Loader2 size={14} className="mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Invite'
                    )}
                  </Button>
                  <Button 
                    onClick={() => setShowInviteForm(false)}
                    variant="ghost"
                    size="sm"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="p-4 bg-brand-primary/10 border border-brand-primary/20 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Trophy size={16} className="text-brand-accent" />
              <span className="text-sm font-medium text-brand-text">How it works</span>
            </div>
            <ul className="text-sm text-slate-400 space-y-1">
              <li>• Share your referral code with friends</li>
              <li>• They sign up and subscribe to a paid plan</li>
              <li>• You earn $30 for each successful referral</li>
              <li>• No limit on the number of referrals</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Users size={20} className="text-brand-accent" />
            <span>Referral History</span>
          </CardTitle>
          <CardDescription>Track your referrals and earnings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {referralData.history.map((referral) => (
              <div key={referral.id} className="flex items-center justify-between p-3 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-accent rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {referral.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-brand-text">{referral.name}</div>
                    <div className="text-xs text-slate-400">{referral.email}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <div className="text-sm font-medium text-brand-text">${referral.earnings}</div>
                    <div className="text-xs text-slate-400">{new Date(referral.date).toLocaleDateString()}</div>
                  </div>
                  {referral.status === 'pending' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => activateReferral(referral.id)}
                      className="text-green-400 hover:text-green-300"
                    >
                      <DollarSign size={14} className="mr-1" />
                      Activate
                    </Button>
                  )}
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    referral.status === 'active' ? 'bg-green-400/20 text-green-400' :
                    referral.status === 'pending' ? 'bg-yellow-400/20 text-yellow-400' :
                    'bg-red-400/20 text-red-400'
                  }`}>
                    {referral.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReferralsTab;
